/* Bulletins — publish a New Citizen update once and it lands everywhere at once:
   the hub news feed and every player's main menu (What's New rail), plus the
   welcome-back digest for returning players. Includes the opt-in reach stats so the
   owner can see the consent-based audience grow. Platform admin only. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Loader2, Users, UserCheck, UserX, Radio, RefreshCcw, BellRing, FileSearch, Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#0A0A0C] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange transition-all";
const labelCls = "font-mono text-[9px] font-bold tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";
const card = "border border-white/[0.08] bg-[#0A0A0C] p-6 flex flex-col gap-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]";

export function BulletinsPanel({ sid }) {
  const [stats, setStats] = useState(null);
  const [bulletins, setBulletins] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", pointsText: "" });
  const [busy, setBusy] = useState(false);
  const [theme, setTheme] = useState("");
  const [leverBusy, setLeverBusy] = useState("");

  const lever = async (key, fn) => {
    if (!sid || leverBusy) return;
    setLeverBusy(key);
    try {
      const r = await fn();
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLeverBusy(""); }
  };

  const load = useCallback(() => {
    api.get("/admin/nc/marketing/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    api.get("/nc/bulletins").then((r) => setBulletins(r.data.bulletins || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const publish = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/nc/bulletins", {
        title: form.title, body: form.body,
        points: form.pointsText.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(r.data.message);
      setForm({ title: "", body: "", pointsText: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className={card} data-testid="bulletins-panel">
      <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
        <Megaphone className="w-3.5 h-3.5" /> Update Bulletins & Player Opt-ins
      </p>
      <p className="font-mono text-[9px] text-zinc-400 leading-relaxed -mt-2">
        Publish once — the bulletin lands on the hub and in every player's main menu within 90 seconds, and greets
        returning players in their welcome-back digest. Players opt in to update news at registration or in their
        community profile; the consented audience below is who future announcement channels (e.g. Discord) will reach.
        Email stays transactional-only — no marketing blasts leave the platform mailer.
      </p>

      {/* reach stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            [UserCheck, stats.opted_in_users, "opted-in citizens", "text-[#00E676]", "opted"],
            [Users, stats.opted_in_players_linked, "linked players", "text-nc-blue", "linked"],
            [UserX, stats.players_lapsed_7d, "lapsed 7+ days", stats.players_lapsed_7d ? "text-amber-300" : "text-zinc-500", "lapsed"],
            [Radio, stats.players_active_7d, "active this week", "text-zinc-300", "active"],
          ].map(([Icon, v, label, cls, id]) => (
            <div key={id} data-testid={`bulletins-stat-${id}`} className="border border-white/[0.08] bg-white/[0.02] px-4 py-3">
              <p className={`font-sans font-bold text-2xl ${cls}`}>{v}</p>
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-zinc-500 mt-1 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</p>
            </div>
          ))}
        </div>
      )}

      {/* publish form */}
      <div className="border border-white/[0.08] bg-white/[0.02] p-5 flex flex-col gap-4">
        <p className="font-mono text-[9px] font-bold tracking-[0.25em] text-zinc-400 uppercase flex items-center gap-2">
          <BellRing className="w-3.5 h-3.5 text-nc-orange" /> Publish an update
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><span className={labelCls}>Title</span><input data-testid="bulletin-title" className={inputCls} maxLength={80} placeholder="v4.2 — Casino Heist Update" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><span className={labelCls}>Highlights — one per line, max 6 (optional)</span><input data-testid="bulletin-points" className={inputCls} placeholder="New heist, 2 vehicles, balance pass" value={form.pointsText} onChange={(e) => setForm({ ...form, pointsText: e.target.value })} /></div>
          <div className="sm:col-span-2"><span className={labelCls}>Body — what changed, why they should come back</span><textarea data-testid="bulletin-body" rows={3} maxLength={2000} className={`${inputCls} resize-none`} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="bulletin-publish" disabled={busy || form.title.trim().length < 3 || form.body.trim().length < 10} onClick={publish}
            className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-3 transition-colors w-fit flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} Publish Bulletin
          </button>
          <span className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.15em]">max 5 per day · every player's menu updates live</span>
        </div>
      </div>

      {/* city levers — mysteries & market shocks for the selected server */}
      <div className="border border-white/[0.08] bg-white/[0.02] p-5 flex flex-col gap-4">
        <p className="font-mono text-[9px] font-bold tracking-[0.25em] text-zinc-400 uppercase flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-nc-orange" /> City Levers — live world events
        </p>
        <p className="font-mono text-[9px] text-zinc-500 leading-relaxed -mt-2">
          Fired at the currently selected server in this deck. Both auto-publish a bulletin so the city knows.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-white/[0.06] bg-black/30 p-4 flex flex-col gap-2.5">
            <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-amber-300 flex items-center gap-2"><FileSearch className="w-3.5 h-3.5" /> Weekly cold case</p>
            <input data-testid="mystery-theme" className={inputCls} maxLength={80} placeholder="Optional theme (e.g. the docks, a missing heist crew)" value={theme} onChange={(e) => setTheme(e.target.value)} />
            <button data-testid="mystery-generate" disabled={leverBusy === "mystery"}
              onClick={() => lever("mystery", () => api.post("/admin/nc/mysteries/generate", {}, { params: { server_id: sid, theme } }))}
              className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-black font-mono text-[9px] tracking-[0.2em] uppercase px-4 py-2.5 transition-colors flex items-center justify-center gap-2">
              {leverBusy === "mystery" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSearch className="w-3.5 h-3.5" />} Generate mystery — $2,500 bounty
            </button>
            <p className="font-mono text-[8px] text-zinc-600">AI-written whodunit · evidence board appears in Forensics · first correct accusation wins</p>
          </div>
          <div className="border border-white/[0.06] bg-black/30 p-4 flex flex-col gap-2.5">
            <p className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-sky-300 flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Market shock — 24h</p>
            <div className="grid grid-cols-3 gap-1.5">
              {[["bank_run", "Bank Run"], ["gold_rush", "Gold Rush"], ["blackout", "Blackout"]].map(([kind, label]) => (
                <button key={kind} data-testid={`shock-${kind}`} disabled={!!leverBusy}
                  onClick={() => lever(kind, () => api.post(`/nc/servers/${sid}/market/shock`, { kind }))}
                  className="border border-sky-400/30 text-sky-300 hover:bg-sky-400/10 disabled:opacity-40 font-mono text-[8px] uppercase tracking-[0.1em] px-2 py-2.5 transition-colors">
                  {label}
                </button>
              ))}
            </div>
            <p className="font-mono text-[8px] text-zinc-600">every listing on the exchange drifts harder · menu banner shows it live</p>
          </div>
        </div>
      </div>

      {/* recent bulletins */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <p className="font-mono text-[9px] font-bold tracking-[0.25em] text-zinc-500 uppercase">Recent bulletins</p>
          <button data-testid="bulletins-refresh" onClick={load} className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-500 hover:text-nc-orange transition-colors flex items-center gap-1"><RefreshCcw className="w-3 h-3" /> Refresh</button>
        </div>
        {bulletins.length === 0 && <p className="font-mono text-[10px] text-zinc-500">Nothing published yet — your first update will appear here and in every player's menu.</p>}
        {bulletins.map((b) => (
          <div key={b.id} data-testid="bulletin-row" className="border border-white/[0.06] bg-white/[0.02] px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-sans font-bold text-sm text-zinc-100">{b.title}</p>
              <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-500">{new Date(b.published_at).toLocaleString()} · {b.published_by}</span>
            </div>
            <p className="font-mono text-[10px] text-zinc-400 mt-1 leading-relaxed">{b.body}</p>
            {(b.points || []).length > 0 && <p className="font-mono text-[9px] text-zinc-500 mt-1">{b.points.join(" · ")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
